import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { pendingCount, runSync, type SyncState } from '@/sync/client';
import { SYNC_DEBOUNCE_MS } from '@/constants/app';
import { db } from '@/db/db';
import { getLocal, getPrefs, listSessions, setLocal, setPrefs } from '@/db/repo';
import { requestPersistentStorage } from '@/db/persist';
import { rollUpOldAttempts } from '@/domain/practice';
import type {
  Attempt,
  AttemptRollup,
  CategoryLevel,
  Constant,
  Formula,
  Item,
  LocalSettings,
  NotebookEntry,
  Progress,
  Session,
  SyncedPrefs,
} from '@/domain/types';

export type PanelName = 'periodic' | 'library' | 'calculator' | 'whiteboard' | null;
export type ThemeName = 'light' | 'dark';

export interface AppStateValue {
  ready: boolean;
  sessions: Session[];
  items: Item[];
  progress: Map<string, Progress>;
  levels: Map<string, CategoryLevel>;
  notebook: NotebookEntry[];
  attempts: Attempt[];
  rollups: AttemptRollup[];
  formulas: Formula[];
  constants: Constant[];
  local: LocalSettings | null;
  prefs: SyncedPrefs | null;
  theme: ThemeName;
  panel: PanelName;
  /**
   * A second panel, shown over the page while the docked one stays where it
   * is — so the table can be checked without losing the document being read.
   * Never persisted: it is a glance, not a layout.
   */
  overlayPanel: PanelName;
  setOverlayPanel: (panel: PanelName) => void;
  /** True while a timed exam is in progress, which locks the Library panel. */
  examInProgress: boolean;
  setExamInProgress: (value: boolean) => void;

  syncState: SyncState;
  syncMessage: string | null;
  pending: number;
  syncNow: () => Promise<void>;

  setPanel: (panel: PanelName) => void;
  reload: () => Promise<void>;
  updateLocal: (patch: Partial<LocalSettings>) => Promise<void>;
  updatePrefs: (patch: Partial<Omit<SyncedPrefs, 'id'>>) => Promise<void>;
  toast: (message: string) => void;
  toastMessage: string | null;
}

const AppStateContext = createContext<AppStateValue | null>(null);

function systemTheme(): ThemeName {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const alive = <T extends { deleted: boolean }>(rows: T[]): T[] => rows.filter((r) => !r.deleted);

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [progress, setProgress] = useState<Map<string, Progress>>(new Map());
  const [levels, setLevels] = useState<Map<string, CategoryLevel>>(new Map());
  const [notebook, setNotebook] = useState<NotebookEntry[]>([]);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [rollups, setRollups] = useState<AttemptRollup[]>([]);
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [constants, setConstants] = useState<Constant[]>([]);
  const [local, setLocalState] = useState<LocalSettings | null>(null);
  const [prefs, setPrefsState] = useState<SyncedPrefs | null>(null);
  const [panel, setPanel] = useState<PanelName>(null);
  const [overlayPanel, setOverlayPanel] = useState<PanelName>(null);
  const panelRestored = useRef(false);
  const [examInProgress, setExamInProgress] = useState(false);
  const [systemIsDark, setSystemIsDark] = useState<ThemeName>(systemTheme);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('signed-out');
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const syncTimer = useRef<number | null>(null);
  const syncing = useRef(false);

  const reload = useCallback(async () => {
    try {
      const [
        nextSessions,
        allItems,
        allProgress,
        allLevels,
        allNotebook,
        allAttempts,
        allRollups,
        allFormulas,
        allConstants,
        nextLocal,
        nextPrefs,
      ] = await Promise.all([
        listSessions(),
        db.items.toArray(),
        db.progress.toArray(),
        db.levels.toArray(),
        db.notebook.toArray(),
        db.attempts.toArray(),
        db.rollups.toArray(),
        db.formulas.toArray(),
        db.constants.toArray(),
        getLocal(),
        getPrefs(),
      ]);
      setSessions(nextSessions);
      setItems(alive(allItems));
      setProgress(new Map(alive(allProgress).map((p) => [p.id, p])));
      setLevels(new Map(alive(allLevels).map((l) => [l.id, l])));
      setNotebook(alive(allNotebook));
      setAttempts(alive(allAttempts));
      setRollups(alive(allRollups));
      setFormulas(alive(allFormulas));
      setConstants(alive(allConstants));
      setLocalState(nextLocal);
      setPrefsState(nextPrefs);
      setReady(true);
    } catch (err) {
      // A read can lose its database — a tab closing it, private-mode eviction.
      // The screen keeps whatever it already has rather than tearing down.
      console.warn('Could not read local data', err);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Collapse attempts past the retention window once per launch.
  useEffect(() => {
    if (!ready) return;
    void rollUpOldAttempts()
      .then((n) => {
        if (n > 0) void reload();
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Ask once per device; Settings explains the answer.
  useEffect(() => {
    if (!local || local.persistentStorage !== 'unknown') return;
    let cancelled = false;
    void requestPersistentStorage()
      .then(async (state) => {
        const next = await setLocal({ persistentStorage: state });
        if (!cancelled) setLocalState(next);
      })
      .catch(() => {
        // The tab may be going away; nothing here is worth surfacing.
      });
    return () => {
      cancelled = true;
    };
  }, [local]);

  // A docked panel is part of the layout, so it comes back on its own. One
  // that was merely open over the page does not: that was a glance.
  useEffect(() => {
    if (!local || panelRestored.current) return;
    panelRestored.current = true;
    if (local.panelsDocked && local.openPanel) setPanel(local.openPanel);
  }, [local]);

  // Remembered device-side, so reopening the app puts the screen back as it
  // was. Writing it is best-effort: a failure here must not break the panel.
  useEffect(() => {
    if (!local || !panelRestored.current) return;
    if (local.openPanel === panel) return;
    void setLocal({ openPanel: panel }).then(setLocalState).catch(() => {});
  }, [panel, local]);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = () => setSystemIsDark(systemTheme());
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const theme: ThemeName = local?.theme === 'system' || !local ? systemIsDark : local.theme;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // The Library is off during exams unless it has been explicitly allowed.
  useEffect(() => {
    if (examInProgress && !local?.allowLibraryInExams) {
      if (panel === 'library') setPanel(null);
      if (overlayPanel === 'library') setOverlayPanel(null);
    }
  }, [examInProgress, local?.allowLibraryInExams, panel, overlayPanel]);

  const syncNow = useCallback(async () => {
    if (syncing.current) return;
    syncing.current = true;
    setSyncState((s) => (s === 'signed-out' ? s : 'syncing'));
    try {
      const outcome = await runSync();
      setSyncState(outcome.state);
      setSyncMessage(outcome.message ?? null);
      if (outcome.pulled > 0) await reload();
      setPending(await pendingCount());
      setLocalState(await getLocal());
    } finally {
      syncing.current = false;
    }
  }, [reload]);

  /** Debounced: a burst of answers results in one sync, not one per answer. */
  const scheduleSync = useCallback(() => {
    if (syncTimer.current) window.clearTimeout(syncTimer.current);
    syncTimer.current = window.setTimeout(() => void syncNow(), SYNC_DEBOUNCE_MS);
  }, [syncNow]);

  // On open, when connectivity returns, and after local writes settle.
  useEffect(() => {
    if (!ready) return;
    void syncNow();
    const onOnline = () => void syncNow();
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', () => setSyncState('offline'));
    return () => window.removeEventListener('online', onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Anything left unpushed schedules a sync. Records that arrived from the
  // server echo back once, are rejected as equal, and then stop counting.
  useEffect(() => {
    if (!ready || !local?.token) return;
    void pendingCount().then((n) => {
      setPending(n);
      if (n > 0) scheduleSync();
    });
  }, [ready, local?.token, local?.lastPushedAt, items, sessions, progress, notebook, scheduleSync]);

  const updateLocal = useCallback(async (patch: Partial<LocalSettings>) => {
    setLocalState(await setLocal(patch));
  }, []);

  const updatePrefs = useCallback(async (patch: Partial<Omit<SyncedPrefs, 'id'>>) => {
    setPrefsState(await setPrefs(patch));
  }, []);

  const toast = useCallback((message: string) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage((current) => (current === message ? null : current)), 3200);
  }, []);

  const value = useMemo<AppStateValue>(
    () => ({
      ready,
      sessions,
      items,
      progress,
      levels,
      notebook,
      attempts,
      rollups,
      formulas,
      constants,
      local,
      prefs,
      theme,
      panel,
      overlayPanel,
      setOverlayPanel,
      examInProgress,
      setExamInProgress,
      syncState,
      syncMessage,
      pending,
      syncNow,
      setPanel,
      reload,
      updateLocal,
      updatePrefs,
      toast,
      toastMessage,
    }),
    [
      ready,
      sessions,
      items,
      progress,
      levels,
      notebook,
      attempts,
      rollups,
      formulas,
      constants,
      local,
      prefs,
      theme,
      panel,
      overlayPanel,
      examInProgress,
      syncState,
      syncMessage,
      pending,
      syncNow,
      reload,
      updateLocal,
      updatePrefs,
      toast,
      toastMessage,
    ],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState(): AppStateValue {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppState must be used inside AppStateProvider');
  return ctx;
}
